import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { arrayMove } from 'react-sortable-hoc';
import { withTranslation } from 'react-i18next';

import ColumnPickerModal from 'components/PreviewTablePicker/ColumnPickerModal';
import {
  firstFreeAttribute,
  isFieldFullyUsed,
  isValueAttribute,
} from 'components/PreviewTablePicker/columnAttributes';
import { SlateDialog } from 'components/Shared/SlateDialog';
import generateUUID from 'helpers/generateUUID';
import { showToast } from 'helpers/toast';
import {
  deriveAfterColumns,
  interleaveColumns,
} from 'components/InspectSettings/sections/columns/inspectColumns';
import { buildSourceGroupFields, sourceOfSuuid } from './sourceGroups';

// True when the draft already holds a value column for this field suuid.
function isValueSelected(draftFields, suuid) {
  return draftFields.some(function(sel) {
    return (sel.suuid === suuid || sel.fieldSUUID === suuid) && isValueAttribute(sel);
  });
}

/**
 * Build hierarchy path (parent titles) for a field
 * Returns array of parent titles from root to immediate parent
 */
export function buildHierarchyPath(field, availableFields) {
  // If field already has parents array (from availableFields), use it directly
  if (field.parents && field.parents.length > 0) {
    return field.parents.map(function(parent) {
      return parent.title;
    });
  }

  // For selected fields, look up the source field in availableFields to get parents
  if (availableFields && (field.suuid || field.fieldSUUID)) {
    var fieldSuuid = field.suuid || field.fieldSUUID;
    var sourceField = availableFields.find(function(f) {
      return f.suuid === fieldSuuid;
    });
    if (sourceField && sourceField.parents && sourceField.parents.length > 0) {
      return sourceField.parents.map(function(parent) {
        return parent.title;
      });
    }
  }

  return [];
}

/**
 * Column picker dialog: two-panel field picker plus live preview. Mount it
 * only while open; it copies selectedFields into draftFields once and hands
 * the result back through onApply.
 */
export class FieldColumnPicker extends Component {
  constructor(props) {
    super(props);
    // Source groups replace the flat tree; built once because the picker remounts on every open.
    if (props.sourceGroups) {
      this._groupFields = buildSourceGroupFields(props.sourceGroups);
    }
    this.state = {
      draftFields: props.fixedColumns
        ? interleaveColumns(props.fixedColumns, props.selectedFields)
        : props.selectedFields.slice(),
      bulkAddDialogOpen: false,
      bulkAddParentField: null,
      bulkAddSearchContext: null,
      bulkAddMode: 'all',
      removeAllDialogOpen: false,
    };
  }

  getAvailableFields() {
    return this._groupFields || this.props.availableFields;
  }

  /**
   * The single place a column object is built from an available field; the
   * click, drop and bulk-add paths all go through it.
   */
  buildColumn(field, attribute, parent) {
    var column = {
      cuuid: generateUUID(),
      sourceCuuid: field.sourceCuuid || field.cuuid,
      title: field.title,
      suuid: field.suuid,
      fieldSUUID: field.suuid,
      fieldAttribute: attribute,
      parent: parent,
      hierarchyPath: buildHierarchyPath(field, this.getAvailableFields()),
      type: field.type,
      meta: field.meta || false,
      recordEntryAttribute: field.recordEntryAttribute,
    };
    if (this.props.sourceGroups) {
      column.source = sourceOfSuuid(field.suuid);
    }
    return column;
  }

  handleApply = function() {
    this.props.onApply(
      this.props.fixedColumns
        ? deriveAfterColumns(this.state.draftFields)
        : this.state.draftFields,
    );
  }.bind(this);

  handleReorder = function(fromIndex, toIndex) {
    if (fromIndex === toIndex) return;
    if (this.state.draftFields[fromIndex] && this.state.draftFields[fromIndex].locked) return;
    var newFields = arrayMove(this.state.draftFields, fromIndex, toIndex);
    this.setState({ draftFields: newFields });
  }.bind(this);

  handleRemoveColumn = function(cuuid) {
    var newFields = this.state.draftFields.filter(function(field) {
      return field.cuuid !== cuuid;
    });
    this.setState({ draftFields: newFields });
  }.bind(this);

  handleRemoveAllColumns = function() {
    this.setState({ removeAllDialogOpen: true });
  }.bind(this);

  handleRemoveAllConfirm = function() {
    var kept = this.state.draftFields.filter(function(field) {
      return field.locked;
    });
    this.setState({ draftFields: kept, removeAllDialogOpen: false });
  }.bind(this);

  handleRemoveAllClose = function() {
    this.setState({ removeAllDialogOpen: false });
  }.bind(this);

  handleAttributeChange = function(cuuid, attribute) {
    var newFields = this.state.draftFields.map(function(field) {
      if (field.cuuid !== cuuid) return field;
      return Object.assign({}, field, { fieldAttribute: attribute });
    });
    this.setState({ draftFields: newFields });
  }.bind(this);

  handleFieldSelect = function(field) {
    if (field.disable) return;
    this.handleFieldDrop(field, this.state.draftFields.length);
  }.bind(this);

  handleFieldDrop = function(field, insertIndex) {
    var draftFields = this.state.draftFields;
    var isRecordListView = this.props.isRecordListView;

    // Every attribute of this field is already a column; nothing left to add.
    var attribute = firstFreeAttribute(field, draftFields, isRecordListView);
    if (!attribute) return;

    var newColumn = this.buildColumn(field, attribute, field.parent);

    // Insert at the specified index
    var newFields = draftFields.slice();
    newFields.splice(insertIndex, 0, newColumn);
    this.setState({ draftFields: newFields });
  }.bind(this);

  /**
   * Recursively collect leaf fields from a parent's children tree.
   * If filterSuuids is provided (a Set), only includes leaves whose suuid is in the set.
   * Returns { eligible: [...], alreadySelected: [...] }
   */
  collectEligibleLeaves = function(parentField, filterSuuids) {
    var draftFields = this.state.draftFields;
    var eligible = [];
    var alreadySelected = [];

    var walk = function(children) {
      if (!children) return;
      for (let i = 0; i < children.length; i++) {
        let child = children[i];
        // Skip description fields
        if (child.type === 'description') continue;
        // If has children, recurse into them
        if (child.children && child.children.length > 0) {
          // Non-group fields with children (e.g. records) are also addable
          if (child.type !== 'group') {
            if (filterSuuids && !filterSuuids.has(child.suuid)) {
              // skip this field but still recurse
            } else {
              let isSelected = isValueSelected(draftFields, child.suuid);
              if (isSelected) {
                alreadySelected.push(child);
              } else {
                eligible.push(child);
              }
            }
          }
          walk(child.children);
          continue;
        }
        // If filtering by search, skip fields not in the visible set
        if (filterSuuids && !filterSuuids.has(child.suuid)) continue;
        // Check if already selected
        let isSelected = isValueSelected(draftFields, child.suuid);
        if (isSelected) {
          alreadySelected.push(child);
        } else {
          eligible.push(child);
        }
      }
    };
    walk(parentField.children);

    return { eligible: eligible, alreadySelected: alreadySelected };
  }.bind(this);

  handleBulkAdd = function(parentField, visibleSuuids, searchQuery) {
    var hasSearch = searchQuery && searchQuery.trim().length > 0;
    this.setState({
      bulkAddDialogOpen: true,
      bulkAddParentField: parentField,
      bulkAddSearchContext: hasSearch ? { visibleSuuids: visibleSuuids, searchQuery: searchQuery } : null,
      bulkAddMode: hasSearch ? 'filtered' : 'all',
    });
  }.bind(this);

  handleBulkAddConfirm = function() {
    var parentField = this.state.bulkAddParentField;
    var bulkAddMode = this.state.bulkAddMode;
    var bulkAddSearchContext = this.state.bulkAddSearchContext;
    var draftFields = this.state.draftFields;
    var t = this.props.t;

    if (!parentField) return;

    var filterSuuids = (bulkAddMode === 'filtered' && bulkAddSearchContext)
      ? bulkAddSearchContext.visibleSuuids
      : null;

    var result = this.collectEligibleLeaves(parentField, filterSuuids);

    if (result.eligible.length === 0) {
      showToast.warning(t('settings:noColumnsToAdd'));
      this.handleBulkAddClose();
      return;
    }

    var self = this;
    var newColumns = result.eligible.map(function(field) {
      return self.buildColumn(
        field,
        field.fieldAttribute || 'value',
        typeof field.parent === 'string' ? field.parent : undefined
      );
    });

    this.setState({ draftFields: draftFields.concat(newColumns) });
    showToast.success(t('settings:columnsAdded', { count: newColumns.length }));
    this.handleBulkAddClose();
  }.bind(this);

  handleBulkAddClose = function() {
    this.setState({
      bulkAddDialogOpen: false,
      bulkAddParentField: null,
      bulkAddSearchContext: null,
      bulkAddMode: 'all',
    });
  }.bind(this);

  handleBulkAddModeChange = function(mode) {
    this.setState({ bulkAddMode: mode });
  }.bind(this);

  render() {
    var availableFields = this.getAvailableFields();
    var allFields = this._groupFields || this.props.allFields;
    var isRecordListView = this.props.isRecordListView;
    var recordsFieldUniquenessIdentifier = this.props.recordsFieldUniquenessIdentifier;
    var recordFieldTitle = this.props.recordFieldTitle;
    var t = this.props.t;

    var draftFields = this.state.draftFields;
    var bulkAddDialogOpen = this.state.bulkAddDialogOpen;
    var bulkAddParentField = this.state.bulkAddParentField;
    var bulkAddSearchContext = this.state.bulkAddSearchContext;
    var bulkAddMode = this.state.bulkAddMode;

    // Mark already-selected fields as disabled in the available fields tree,
    // so the left panel reflects in-progress edits
    var fieldsToCheck = draftFields;
    var customCount = draftFields.filter(function(field) {
      return !field.locked;
    }).length;
    var markSelectedFields = function(fields) {
      if (!fields) return fields;
      return fields.map(function(field) {
        // Grey the field out only when every attribute it allows is already a
        // column; a field with its value column selected can still supply user
        // and timestamp columns.
        var isAlreadySelected = isFieldFullyUsed(
          field,
          fieldsToCheck,
          isRecordListView,
        );
        var markedField = isAlreadySelected
          ? Object.assign({}, field, { disable: true, alreadySelected: true })
          : field;
        // Recursively mark children
        if (markedField.children && markedField.children.length > 0) {
          markedField = Object.assign({}, markedField, {
            children: markSelectedFields(markedField.children)
          });
        }
        return markedField;
      });
    };

    var markedAvailableFields = availableFields ? markSelectedFields(availableFields) : [];

    // Ensure draft fields have hierarchy paths computed
    var draftFieldsWithPaths = draftFields.map(function(field) {
      if (!field.hierarchyPath && field.parent && availableFields) {
        return Object.assign({}, field, {
          hierarchyPath: buildHierarchyPath(field, availableFields)
        });
      }
      return field;
    });

    // Dialog actions
    var dialogActions = [
      {
        label: t('btn:cancel'),
        onClick: this.props.onClose,
        variant: 'secondary',
      },
      {
        label: this.props.applyLabel || t('settings:applyColumns'),
        onClick: this.handleApply,
        variant: 'primary',
      },
    ];

    return (
      <div>
        <SlateDialog
          open={true}
          onClose={this.props.onClose}
          title={this.props.title || t('settings:editColumns')}
          actions={dialogActions}
          maxWidth="md"
          contentPadding={false}
          mobileFixedHeight
        >
          <ColumnPickerModal
            draftFields={draftFieldsWithPaths}
            availableFields={markedAvailableFields}
            allFields={allFields}
            selectedFields={draftFields}
            isRecordListView={isRecordListView}
            recordsFieldUniquenessIdentifier={recordsFieldUniquenessIdentifier}
            recordFieldTitle={recordFieldTitle}
            onFieldClick={this.handleFieldSelect}
            onFieldDrop={this.handleFieldDrop}
            onRemove={this.handleRemoveColumn}
            onRemoveAll={this.handleRemoveAllColumns}
            onReorder={this.handleReorder}
            onBulkAdd={this.handleBulkAdd}
            onAttributeChange={this.handleAttributeChange}
            t={t}
          />
        </SlateDialog>

        {bulkAddDialogOpen && this.renderBulkAddDialog(
          bulkAddParentField, bulkAddSearchContext, bulkAddMode, draftFields, t
        )}

        {this.state.removeAllDialogOpen && (
          <SlateDialog
            open={true}
            onClose={this.handleRemoveAllClose}
            title={t('settings:removeAllTitle')}
            actions={[
              {
                label: t('btn:cancel'),
                onClick: this.handleRemoveAllClose,
                variant: 'secondary',
              },
              {
                label: t('settings:removeAllConfirm', { count: customCount }),
                onClick: this.handleRemoveAllConfirm,
                variant: 'destructive',
              },
            ]}
            maxWidth="xs"
          >
            <p style={{ margin: '0 0 0 0', color: 'var(--sl-card-title)', fontSize: 14 }}>
              {t('settings:removeAllMessage', { count: customCount })}
            </p>
          </SlateDialog>
        )}
      </div>
    );
  }

  renderBulkAddDialog(parentField, searchContext, mode, draftFields, t) {
    if (!parentField) return null;

    var self = this;
    var filterSuuids = (mode === 'filtered' && searchContext)
      ? searchContext.visibleSuuids
      : null;

    var result = this.collectEligibleLeaves(parentField, filterSuuids);
    var eligibleCount = result.eligible.length;

    // Also compute counts for both modes when search context exists
    var filteredCount = 0;
    var allCount = 0;
    if (searchContext) {
      var filteredResult = this.collectEligibleLeaves(parentField, searchContext.visibleSuuids);
      filteredCount = filteredResult.eligible.length;
      var allResult = this.collectEligibleLeaves(parentField, null);
      allCount = allResult.eligible.length;
    }

    // Build field tree for display
    var renderFieldTree = function(children, depth) {
      if (!children) return [];
      var items = [];
      for (let i = 0; i < children.length; i++) {
        let child = children[i];
        // Skip description fields
        if (child.type === 'description') continue;
        // If filtering, skip fields not visible
        if (filterSuuids && !filterSuuids.has(child.suuid)) continue;

        if (child.children && child.children.length > 0) {
          if (child.type === 'group') {
            // Pure group header (not addable)
            items.push(
              React.createElement('div', {
                key: child.suuid,
                className: 'bulk-add-group-header',
                style: { paddingLeft: 12 + depth * 20 },
              },
                React.createElement('span', null, child.title),
                child.type && React.createElement('span', {
                  className: 'bulk-add-field-item-type',
                }, child.type)
              )
            );
          } else {
            // Non-group with children (e.g. records) -- render as addable item
            let isParentSelected = isValueSelected(draftFields, child.suuid);
            let parentItemClass = 'bulk-add-field-item';
            if (isParentSelected) {
              parentItemClass += ' bulk-add-field-item--disabled';
            }
            items.push(
              React.createElement('div', {
                key: child.suuid,
                className: parentItemClass,
                style: { paddingLeft: 12 + depth * 20 },
              },
                React.createElement('div', { className: 'bulk-add-field-item-left' },
                  React.createElement('span', { className: 'bulk-add-field-item-title' }, child.title),
                  isParentSelected && React.createElement('span', {
                    className: 'bulk-add-field-item-badge-added',
                  }, '(' + t('settings:bulkAddAlreadyAdded') + ')')
                ),
                child.type && React.createElement('span', {
                  className: 'bulk-add-field-item-type',
                }, child.type)
              )
            );
          }
          // Recurse into children either way
          items = items.concat(renderFieldTree(child.children, depth + 1));
        } else {
          // Leaf field
          let isSelected = isValueSelected(draftFields, child.suuid);
          let itemClass = 'bulk-add-field-item';
          if (isSelected) {
            itemClass += ' bulk-add-field-item--disabled';
          }

          items.push(
            React.createElement('div', {
              key: child.suuid,
              className: itemClass,
              style: { paddingLeft: 12 + depth * 20 },
            },
              React.createElement('div', { className: 'bulk-add-field-item-left' },
                React.createElement('span', { className: 'bulk-add-field-item-title' }, child.title),
                isSelected && React.createElement('span', {
                  className: 'bulk-add-field-item-badge-added',
                }, '(' + t('settings:bulkAddAlreadyAdded') + ')')
              ),
              child.type && React.createElement('span', {
                className: 'bulk-add-field-item-type',
              }, child.type)
            )
          );
        }
      }
      return items;
    };

    var fieldTreeItems = renderFieldTree(parentField.children, 0);

    var dialogActions = [
      {
        label: t('btn:cancel'),
        onClick: this.handleBulkAddClose,
        variant: 'secondary',
      },
      {
        label: t('settings:bulkAddConfirm', { count: eligibleCount }),
        onClick: this.handleBulkAddConfirm,
        variant: 'primary',
        disabled: eligibleCount === 0,
      },
    ];

    return React.createElement(SlateDialog, {
      open: true,
      onClose: this.handleBulkAddClose,
      title: t('settings:bulkAddTitle'),
      actions: dialogActions,
      maxWidth: 'sm',
    },
      React.createElement('div', { className: 'bulk-add-header' },
        React.createElement('div', { className: 'bulk-add-header-field' },
          React.createElement('span', {
            className: 'material-symbols-outlined bulk-add-header-field-icon',
          }, 'folder'),
          React.createElement('span', { className: 'bulk-add-header-field-title' }, parentField.title),
          parentField.type && React.createElement('span', {
            className: 'bulk-add-field-item-type',
          }, parentField.type)
        ),
        React.createElement('div', { className: 'bulk-add-header-message' },
          t('settings:bulkAddMessage', { name: parentField.title })
        )
      ),
      searchContext && React.createElement('div', { className: 'bulk-add-mode-toggle' },
        React.createElement('button', {
          type: 'button',
          className: 'bulk-add-mode-button' + (mode === 'filtered' ? ' bulk-add-mode-button--active' : ''),
          onClick: function() { self.handleBulkAddModeChange('filtered'); },
        }, t('settings:bulkAddSearchResults', { count: filteredCount })),
        React.createElement('button', {
          type: 'button',
          className: 'bulk-add-mode-button' + (mode === 'all' ? ' bulk-add-mode-button--active' : ''),
          onClick: function() { self.handleBulkAddModeChange('all'); },
        }, t('settings:bulkAddAllFields', { count: allCount }))
      ),
      React.createElement('div', { className: 'bulk-add-field-list' },
        fieldTreeItems.length > 0
          ? fieldTreeItems
          : React.createElement('div', { className: 'bulk-add-empty' }, t('settings:noColumnsToAdd'))
      )
    );
  }
}

FieldColumnPicker.propTypes = {
  selectedFields: PropTypes.array.isRequired,
  availableFields: PropTypes.array.isRequired,
  allFields: PropTypes.array,
  fixedColumns: PropTypes.array,
  sourceGroups: PropTypes.array,
  isRecordListView: PropTypes.bool,
  recordsFieldUniquenessIdentifier: PropTypes.string,
  recordFieldTitle: PropTypes.string,
  title: PropTypes.string,
  applyLabel: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

FieldColumnPicker.defaultProps = {
  selectedFields: [],
  availableFields: [],
  allFields: [],
  isRecordListView: false,
  recordsFieldUniquenessIdentifier: '',
};

export default withTranslation()(FieldColumnPicker);
